import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";
import Pagination, { usePaginatedSlice } from "@/components/Pagination";

const CATEGORIES = ["Toutes", "Les bases", "Comparatif", "Fiscalité"];

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [cat, setCat] = useState("Toutes");
  const [, setParams] = useSearchParams();
  useEffect(() => { api.get("/articles").then(({ data }) => setArticles(data)).catch(() => {}); }, []);
  const filtered = useMemo(() => cat === "Toutes" ? articles : articles.filter((a) => a.category === cat), [articles, cat]);

  const resetPage = () => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("page");
      return p;
    });
  };

  const { page, totalPages, start, end } = usePaginatedSlice(filtered.length, 12);
  const pageItems = filtered.slice(start, end);

  return (
    <>
      <SEO title="Guides & fiscalité de la vente à terme — venteaterme.fr" description="Guides complets, comparatifs viager, fiscalité vendeur. Toutes les ressources pour comprendre la vente à terme immobilière." />
      <section className="pt-24 pb-12 bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-accent-brand font-bold mb-4">Ressources</div>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight">Guides & fiscalité</h1>
          <p className="text-cream/80 mt-6 max-w-2xl">Comprendre la vente à terme, ses avantages fiscaux et ses protections juridiques.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <Breadcrumbs items={[
            { href: "/", label: "Accueil" },
            { href: "/blog", label: "Guides" },
          ]} />
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORIES.map((c) => (
              <button key={c} onClick={() => { setCat(c); resetPage(); }} data-testid={`blog-cat-${c}`}
                className={`text-xs uppercase tracking-wider font-bold px-4 py-2 border transition-colors ${cat === c ? "bg-navy text-cream border-navy" : "bg-white text-navy border-navy/20 hover:border-navy"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pageItems.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 80}>
                <Link to={`/blog/${a.slug}`} data-testid={`article-card-${a.slug}`}
                  className="group block bg-white border border-navy/10 card-lift overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden bg-navy/5">
                    <img src={a.cover_image} alt={a.title} loading="lazy" decoding="async" width="800" height="500"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-7">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-accent-brand font-bold mb-3">{a.category}</div>
                    <h2 className="font-serif text-xl text-navy leading-snug mb-3 group-hover:text-accent-brand transition-colors text-balance">{a.title}</h2>
                    <p className="text-sm text-navy/70 leading-relaxed">{a.excerpt}</p>
                    <div className="text-xs text-navy/50 mt-4">{a.reading_time} min de lecture</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} basePath="/blog" />
        </div>
      </section>
    </>
  );
}
